import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, notification, Skeleton, Tag, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import {
    BlockedItemDto,
    PagedResult,
    UnblockMenuItemRequest,
    UnblockRecipeItemRequest,
} from '../../types/itemblocktypes';
import {
    getBlockedItems,
    unblockMenuItem,
    unblockRecipeItem,
} from '../../services/itemblockservice';
import FormButton from '../common/formbutton';
import { getAllStoresForDropdown } from '../../services/storeserivce';
import { StoreDropdownDto } from '../../types/storetypes';

const { Search } = Input;
const { Option } = Select;

interface Props {
    userGuid: string;
    activeTab: string;
}

const BlockedItemsTab: React.FC<Props> = ({ userGuid, activeTab }) => {
    const [items, setItems] = useState<BlockedItemDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<'menu' | 'recipe' | undefined>(undefined);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [stores, setStores] = useState<StoreDropdownDto[]>([]);
    const [storeFilter, setStoreFilter] = useState<string | undefined>(undefined);
    const [unblockingId, setUnblockingId] = useState<string | null>(null);

    const fetchItems = async (
        page = pageNumber,
        size = pageSize,
        search = searchTerm,
        f = filter,
        storeNum = storeFilter
    ) => {
        setLoading(true);
        try {
            const res = await getBlockedItems(page, size, search, f ?? 'all', storeNum);
            const data: PagedResult<BlockedItemDto> = res.data;
            setItems(data.items);
            setTotalCount(data.totalCount);
            setPageNumber(data.pageNumber);
            setPageSize(data.pageSize);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load blocked items.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'blocked') {
            fetchItems(1, pageSize, searchTerm, filter, storeFilter);
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        getAllStoresForDropdown().then(res => setStores(res.data));
    }, []);

    useEffect(() => {
        fetchItems();
    }, [pageSize, filter, storeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchTerm(value);
            fetchItems(1, pageSize, value, filter, storeFilter);
        }, 400),
        [pageSize, filter, storeFilter] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleUnblock = async (record: BlockedItemDto) => {
        setUnblockingId(`${record.itemType}-${record.itemGuid}-${record.storeGuid}`);
        try {
            if (record.itemType === 'Menu') {
                const request: UnblockMenuItemRequest = {
                    menuGuid: record.itemGuid,
                    storeGuids: [record.storeGuid],
                    userGuid,
                    reason: 'Unblocked via ItemBlock tab',
                };
                await unblockMenuItem(request);
            } else {
                const request: UnblockRecipeItemRequest = {
                    recipeGuid: record.itemGuid,
                    storeGuids: [record.storeGuid],
                    userGuid,
                    reason: 'Unblocked via ItemBlock tab',
                };
                await unblockRecipeItem(request);
            }
            notification.success({
                message: 'Success',
                description: `${record.name} (${record.itemType}) unblocked for store ${record.storeNumber}.`,
            });
            fetchItems(pageNumber, pageSize, searchTerm, filter, storeFilter);
        } catch {
            notification.error({ message: 'Error', description: `Failed to unblock ${record.name}.` });
        } finally {
            setUnblockingId(null);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilter(undefined);
        setStoreFilter(undefined);
        setPageNumber(1);
        fetchItems(1, pageSize, '', undefined, undefined);
    };

    const columns: ColumnsType<BlockedItemDto> = [
        {
            title: 'Date Created',
            dataIndex: 'dateCreated',
            key: 'dateCreated',
            render: (val: string) => dayjs(val).format('DD MMM YYYY, HH:mm'),
        },
        { title: 'Item', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Status',
            key: 'status',
            render: () => <Tag color="red">Blocked</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: BlockedItemDto) => (
                <FormButton
                    title="Unblock"
                    onClick={() => handleUnblock(record)}
                    loading={unblockingId === `${record.itemType}-${record.itemGuid}-${record.storeGuid}`}
                />
            ),
        },
    ];

    return (
        <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
                <Search
                    placeholder="Search items"
                    onChange={e => debouncedSearch(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Select
                    value={filter}
                    onChange={val => setFilter(val)}
                    style={{ width: 150 }}
                    allowClear
                    placeholder="Filter by Type"
                >
                    <Option value="menu">Menus</Option>
                    <Option value="recipe">Recipes</Option>
                </Select>
                <Select
                    value={storeFilter}
                    onChange={val => {
                        setStoreFilter(val);
                        fetchItems(1, pageSize, searchTerm, filter, val);
                    }}
                    style={{ width: 250 }}
                    allowClear
                    placeholder="Filter by Store"
                    showSearch
                    optionFilterProp="value"
                    filterOption={(input, option) =>
                        (option?.value as string).toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {stores.map(s => (
                        <Option key={s.storeGuid} value={s.storeNumber}>
                            {s.storeNumber}
                        </Option>
                    ))}
                </Select>
                <Button onClick={clearFilters}>Clear Filters</Button>
            </div>

            {loading && items.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<BlockedItemDto>
                    dataSource={items}
                    columns={columns}
                    loading={loading && items.length > 0}
                    pagination={{
                        current: pageNumber,
                        pageSize: pageSize,
                        total: totalCount,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
                        onChange: (page, size) => {
                            setPageSize(size);
                            fetchItems(page, size, searchTerm, filter, storeFilter);
                        },
                    }}
                    rowKey={record => `${record.itemType}-${record.itemGuid}-${record.storeGuid}`}
                />
            )}
        </>
    );
};

export default BlockedItemsTab;